import {
  calendarDateFromLocalDate,
  calendarDateFromParts,
  calendarDateToLocalDate,
  compareCalendarDates,
  today,
  type CalendarDate,
} from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { ButtonVariant } from '@ui/components/base/button';
import { Calendar } from '@ui/components/base/calendar';
import { Field, FieldLabel, FieldVariant, getFieldLabelVariant } from '@ui/components/base/field';
import { InputGroupAddon, InputGroupButton, InputGroupField, InputGroupInput } from '@ui/components/base/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/base/popover';
import { FieldErrorIndicator, FieldStatusIndicator } from '@ui/components/fields/fieldErrorIndicator';
import { CalendarIcon, XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

type CalendarCaptionLayout = ComponentProps<typeof Calendar>['captionLayout'];

export interface DatePickerProps {
  value: CalendarDate | null;
  onChange: (date: CalendarDate | null) => void;
  onClear?: () => void;
  label?: string;
  variant?: FieldVariant;
  placeholder?: string;
  disabled?: boolean;
  minDate?: CalendarDate;
  maxDate?: CalendarDate;
  dateFormat?: string;
  todayLabel?: string;
  error?: boolean;
  helperText?: string;
  errorMessage?: ReactNode;
  successMessage?: ReactNode;
  clearable?: boolean;
  required?: boolean;
  id?: string;
  className?: string;
  invalidDateMessage?: string;
  errorMessageValues?: Record<string, string | number>;
  captionLayout?: CalendarCaptionLayout;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onInputErrorChange?: (hasInputError: boolean) => void;
}

interface SyncedValue {
  value: CalendarDate | null;
  dateFormat: string;
}

const DEFAULT_DATE_FORMAT = 'dd/MM/yyyy';
export const DEFAULT_INVALID_DATE_MESSAGE = 'error.invalidDate';
const DEFAULT_PLACEHOLDER_MESSAGE = 'datePicker.placeholder';
const DEFAULT_NAVIGATION_END_YEAR = 3000;
const DECEMBER_MONTH = 12;
const LAST_DAY_OF_DECEMBER = 31;

const DEFAULT_MIN_DATE: CalendarDate = calendarDateFromParts(1500, 1, 1);
const DEFAULT_NAVIGATION_END_MONTH: CalendarDate = calendarDateFromParts(
  DEFAULT_NAVIGATION_END_YEAR,
  DECEMBER_MONTH,
  LAST_DAY_OF_DECEMBER
);

// Supported tokens for both formatting and parsing — longest variant listed first so the regex
// alternation and the replace pass both match 'yyyy'/'dd'/'MM' before falling back to 'yy'/'d'/'M'.
const TOKEN_PATTERN = /yyyy|yy|dd|d|MM|M/g;

const pad = (value: number, length: number): string => String(value).padStart(length, '0');

const formatCalendarDate = (date: CalendarDate, pattern: string): string =>
  pattern.replace(TOKEN_PATTERN, (token) => {
    switch (token) {
      case 'yyyy':
        return pad(date.year, 4);
      case 'yy':
        return pad(date.year % 100, 2);
      case 'dd':
        return pad(date.day, 2);
      case 'MM':
        return pad(date.month, 2);
      case 'd':
        return String(date.day);
      case 'M':
        return String(date.month);
      default:
        return token;
    }
  });

const buildPatternMatcher = (pattern: string): { regex: RegExp; tokens: string[] } => {
  const tokens: string[] = [];
  const regexSource = pattern.replace(TOKEN_PATTERN, (token) => {
    tokens.push(token);
    if (token === 'yyyy') {
      return '(\\d{4})';
    }
    return token.length === 2 ? '(\\d{2})' : '(\\d{1,2})';
  });
  return { regex: new RegExp(`^${regexSource}$`), tokens };
};

const parseCalendarDateWithPattern = (value: string, pattern: string): CalendarDate | null => {
  const { regex, tokens } = buildPatternMatcher(pattern);
  const match = regex.exec(value);
  if (!match) {
    return null;
  }

  let day: number | undefined;
  let month: number | undefined;
  let year: number | undefined;
  const centuryBase = Math.floor(today().year / 100) * 100;

  tokens.forEach((token, index) => {
    const raw = Number(match[index + 1]);
    if (token === 'd' || token === 'dd') {
      day = raw;
    } else if (token === 'M' || token === 'MM') {
      month = raw;
    } else if (token === 'yyyy') {
      year = raw;
    } else if (token === 'yy') {
      year = centuryBase + raw;
    }
  });

  if (day === undefined || month === undefined || year === undefined) {
    return null;
  }

  try {
    return calendarDateFromParts(year, month, day);
  } catch {
    return null;
  }
};

const isOutOfRange = (date: CalendarDate, minDate?: CalendarDate, maxDate?: CalendarDate): boolean => {
  if (minDate && compareCalendarDates(date, minDate) < 0) {
    return true;
  }
  if (maxDate && compareCalendarDates(date, maxDate) > 0) {
    return true;
  }
  return false;
};

const canAutoSlashDateFormat = (dateFormat: string): boolean => ['dd/MM/yyyy', 'MM/dd/yyyy'].includes(dateFormat);

const addDateSlashes = (inputValue: string, dateFormat: string): string => {
  if (!canAutoSlashDateFormat(dateFormat)) return inputValue;

  const digits = inputValue.replace(/\D/g, '').slice(0, 8);
  const firstPartLength = 2;
  const secondPartLength = 2;

  if (digits.length <= firstPartLength) return digits;
  if (digits.length <= firstPartLength + secondPartLength) {
    return `${digits.slice(0, firstPartLength)}/${digits.slice(firstPartLength)}`;
  }

  return [
    digits.slice(0, firstPartLength),
    digits.slice(firstPartLength, firstPartLength + secondPartLength),
    digits.slice(firstPartLength + secondPartLength),
  ].join('/');
};

const parseInputDate = (inputValue: string, dateFormat: string): CalendarDate | null => {
  const trimmedValue = inputValue.trim();

  const parsedDate = parseCalendarDateWithPattern(trimmedValue, dateFormat);
  if (parsedDate && formatCalendarDate(parsedDate, dateFormat) === trimmedValue) {
    return parsedDate;
  }

  const fallbackDate = parseCalendarDateWithPattern(trimmedValue, DEFAULT_DATE_FORMAT);
  if (fallbackDate && formatCalendarDate(fallbackDate, DEFAULT_DATE_FORMAT) === trimmedValue) {
    return fallbackDate;
  }

  return null;
};

const DatePicker = ({
  value,
  onChange,
  onClear,
  label,
  variant = FieldVariant.DEFAULT,
  placeholder,
  disabled = false,
  minDate = DEFAULT_MIN_DATE,
  maxDate,
  dateFormat = DEFAULT_DATE_FORMAT,
  todayLabel,
  error: externalError,
  helperText,
  errorMessage,
  successMessage,
  clearable = false,
  required = false,
  id,
  className,
  invalidDateMessage = DEFAULT_INVALID_DATE_MESSAGE,
  errorMessageValues,
  captionLayout = 'dropdown',
  open: openProp,
  onOpenChange,
  onInputErrorChange,
}: DatePickerProps) => {
  const { formatMessage } = useIntl();
  const formattedValue = value ? formatCalendarDate(value, dateFormat) : '';
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const isOpen = openProp ?? internalOpen;
  const [inputValue, setInputValue] = useState<string>(formattedValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasInputError, setHasInputError] = useState<boolean>(false);
  const lastCommittedInputValueRef = useRef<string>(formattedValue);
  const syncedValueRef = useRef<SyncedValue>({ value, dateFormat });
  const inputRef = useRef<HTMLInputElement>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    setInternalOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  useEffect(() => {
    if (openProp) {
      inputRef.current?.focus();
    }
  }, [openProp]);

  useEffect(() => {
    onInputErrorChange?.(hasInputError);
  }, [hasInputError, onInputErrorChange]);

  const isToday = value != null && compareCalendarDates(value, today()) === 0;
  const displayValue = todayLabel && isToday && !isFocused ? todayLabel : inputValue;
  const hasRangeError = value != null && isOutOfRange(value, minDate, maxDate);
  const hasError = hasInputError || (externalError ?? false) || hasRangeError;
  const hasExternalMessage = Boolean(externalError && (errorMessage || helperText));
  const showsInputError = hasInputError && !hasExternalMessage;
  const visibleHelperText = showsInputError ? invalidDateMessage : helperText;
  const visibleErrorMessage = showsInputError ? undefined : errorMessage;
  const visiblePlaceholder = placeholder ?? formatMessage({ id: DEFAULT_PLACEHOLDER_MESSAGE });
  const hasDropdownCaption = captionLayout?.startsWith('dropdown') ?? false;
  const calendarStartMonth = hasDropdownCaption ? calendarDateToLocalDate(minDate) : undefined;
  const calendarEndMonth = maxDate
    ? calendarDateToLocalDate(maxDate)
    : hasDropdownCaption
      ? calendarDateToLocalDate(DEFAULT_NAVIGATION_END_MONTH)
      : undefined;

  useEffect(() => {
    if (syncedValueRef.current.value === value && syncedValueRef.current.dateFormat === dateFormat) {
      return;
    }
    syncedValueRef.current = { value, dateFormat };
    const nextInputValue = value ? formatCalendarDate(value, dateFormat) : '';

    setInputValue(nextInputValue);
    setHasInputError(false);
    lastCommittedInputValueRef.current = nextInputValue;
  }, [value, dateFormat]);

  const handleSelect = (date: Date | undefined) => {
    const nextValue = date ? calendarDateFromLocalDate(date) : null;

    setInputValue(nextValue ? formatCalendarDate(nextValue, dateFormat) : '');
    setHasInputError(false);
    lastCommittedInputValueRef.current = nextValue ? formatCalendarDate(nextValue, dateFormat) : '';
    onChange(nextValue);
    handleOpenChange(false);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    setInputValue('');
    setHasInputError(false);
    lastCommittedInputValueRef.current = '';
    onChange(null);
    onClear?.();
  };

  const commitInputValue = () => {
    const trimmedValue = inputValue.trim();

    if (trimmedValue === lastCommittedInputValueRef.current) {
      return;
    }

    if (!trimmedValue) {
      setInputValue('');
      setHasInputError(false);
      lastCommittedInputValueRef.current = '';
      onChange(null);
      onClear?.();
      return;
    }

    const parsedDate = parseInputDate(trimmedValue, dateFormat);

    if (!parsedDate || isOutOfRange(parsedDate, minDate, maxDate)) {
      setHasInputError(true);
      return;
    }

    setInputValue(formatCalendarDate(parsedDate, dateFormat));
    setHasInputError(false);
    lastCommittedInputValueRef.current = formatCalendarDate(parsedDate, dateFormat);
    onChange(parsedDate);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(addDateSlashes(event.target.value, dateFormat));
    if (hasInputError) setHasInputError(false);
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    commitInputValue();
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    commitInputValue();
  };

  return (
    <Field className={className}>
      {label && (
        <FieldLabel
          htmlFor={id}
          variant={getFieldLabelVariant(variant)}
          className={cn(required && "after:ml-0.5 after:text-destructive after:content-['*']")}
        >
          {label}
        </FieldLabel>
      )}
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <InputGroupField
          className={cn('h-12 rounded-lg', disabled && 'pointer-events-none opacity-50')}
          data-disabled={disabled || undefined}
        >
          <InputGroupInput
            ref={inputRef}
            id={id}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={visiblePlaceholder}
            inputMode="numeric"
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-required={required || undefined}
            className="h-full px-3 font-normal"
          />
          <InputGroupAddon align="inline-end">
            {hasError && !isNil(visibleErrorMessage) && (
              <FieldStatusIndicator status="error">{visibleErrorMessage}</FieldStatusIndicator>
            )}
            {hasError && isNil(visibleErrorMessage) && visibleHelperText && (
              <FieldErrorIndicator messageId={visibleHelperText} values={errorMessageValues} />
            )}
            {!hasError && !isNil(successMessage) && (
              <FieldStatusIndicator status="success">{successMessage}</FieldStatusIndicator>
            )}
            {clearable && (value || inputValue) && !disabled && (
              <InputGroupButton
                variant={ButtonVariant.GHOST}
                size="icon-xs"
                onClick={handleClear}
                aria-label="Clear date"
                className="text-muted-foreground"
              >
                <XIcon className="size-4" />
              </InputGroupButton>
            )}
            <PopoverTrigger
              render={<InputGroupButton variant={ButtonVariant.GHOST} size="icon-xs" />}
              disabled={disabled}
              aria-label="Open calendar"
              type="button"
              className="cursor-pointer hover:bg-transparent focus-visible:ring-0 aria-expanded:bg-transparent"
            >
              <CalendarIcon className="size-4" />
            </PopoverTrigger>
          </InputGroupAddon>
        </InputGroupField>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            mode="single"
            captionLayout={captionLayout}
            startMonth={calendarStartMonth}
            endMonth={calendarEndMonth}
            selected={value ? calendarDateToLocalDate(value) : undefined}
            defaultMonth={value ? calendarDateToLocalDate(value) : undefined}
            onSelect={handleSelect}
            disabled={(date: Date) => isOutOfRange(calendarDateFromLocalDate(date), minDate, maxDate)}
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
};

export { DatePicker };
